import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import api from '../api';
import './QuarterlyHighlights.css';

import { isHighlightWindowOpen } from '../utils/dateUtils';

const QuarterlyHighlights = () => {
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Newsletter State
  const [isGeneratingNewsletter, setIsGeneratingNewsletter] = useState(false);
  const [newsletterContent, setNewsletterContent] = useState(null);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);

  useEffect(() => {
    const fetchHighlights = async () => {
      try {
        const response = await api.get('/api/highlights?months=3&limit=5');
        // Only show up to 5
        const data = response.data.slice(0, 5);
        setHighlights(data);
      } catch (err) {
        console.error('Failed to fetch highlights:', err);
        setError('Failed to load quarterly highlights.');
      } finally {
        setLoading(false);
      }
    };

    fetchHighlights();
  }, []);

  const handleGenerateNewsletter = async () => {
    setIsGeneratingNewsletter(true);
    setShowNewsletterModal(true);
    try {
      const response = await api.get('/api/newsletter');
      setNewsletterContent(response.data.content);
    } catch (err) {
      console.error('Failed to generate newsletter:', err);
      setNewsletterContent('⚠️ Failed to generate the newsletter. Please check if your API keys are configured correctly.');
    } finally {
      setIsGeneratingNewsletter(false);
    }
  };

  if (!isHighlightWindowOpen()) {
    // Only display highlights during the first 5 days of each quarter
    return null;
  }

  if (loading) {
    return (
      <div className="highlights-loader-container">
        <div className="highlights-loader"></div>
        <p>Curating Quarterly Highlights...</p>
      </div>
    );
  }

  if (error || highlights.length === 0) {
    return (
      <div className="highlights-empty">
        <p>No recent major achievements found for this quarter.</p>
      </div>
    );
  }

  // The first item is our "Hero" story, the rest are secondary stories
  const [heroStory, ...secondaryStories] = highlights;

  return (
    <div className="quarterly-highlights-container">
      <div className="highlights-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 className="highlights-title">The Quarterly Chronicle</h2>
          <div className="highlights-subtitle">Top achievements and high-impact MoU activities</div>
        </div>
        <button 
          onClick={handleGenerateNewsletter}
          className="btn-action edit"
          style={{ padding: '8px 16px', background: '#1a73e8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}
        >
          📰 View AI Newsletter
        </button>
      </div>

      <div className="newspaper-grid">
        {heroStory && (
          <div className="story-card hero-story">
            {heroStory.activity.thumbnailUrl && (
              <div className="story-image-container">
                <img src={heroStory.activity.thumbnailUrl} alt={heroStory.activity.title} className="story-image" />
              </div>
            )}
            <div className="story-content">
              <span className="story-meta">{heroStory.mouTitle} • {heroStory.activity.date}</span>
              <h3 className="story-headline">{heroStory.activity.title}</h3>
              <p className="story-excerpt">
                This event achieved a massive milestone with a record impact score of <strong>{heroStory.score}</strong>. 
                With strong community engagement ({heroStory.activity.likesCount} likes), it stands as the quarter's leading activity.
              </p>
              <div className="story-footer">
                <span className="impact-badge">Top Highlight</span>
              </div>
            </div>
          </div>
        )}

        <div className="secondary-stories-grid">
          {secondaryStories.map((story, index) => (
            <div key={story.activity.id || index} className="story-card secondary-story">
              {story.activity.thumbnailUrl && (
                <div className="story-image-container">
                  <img src={story.activity.thumbnailUrl} alt={story.activity.title} className="story-image" />
                </div>
              )}
              <div className="story-content">
                <span className="story-meta">{story.mouTitle}</span>
                <h4 className="story-headline">{story.activity.title}</h4>
                <p className="story-excerpt">
                  Impact Score: {story.score} <br />
                  Engagement: {story.activity.likesCount} likes
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showNewsletterModal && (
        <div className="modal-overlay" style={{ zIndex: 9999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxWidth: '800px', width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e0e0e0' }}>
              <h2>📰 Quarterly Collaboration Digest</h2>
              <button className="btn-close" onClick={() => setShowNewsletterModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', padding: '20px', lineHeight: '1.6', color: '#202124' }}>
              {isGeneratingNewsletter ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner" style={{ margin: '0 auto 16px auto', borderTopColor: '#1a73e8' }}></div>
                  <p>Loading Newsletter...</p>
                </div>
              ) : (
                <div className="markdown-content">
                  <ReactMarkdown>{newsletterContent}</ReactMarkdown>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '16px 20px', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" style={{ padding: '8px 16px', background: '#f1f3f4', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }} onClick={() => setShowNewsletterModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuarterlyHighlights;
